import { useMemo, useState } from 'react';
import { Alert, Button, Drawer, Input, InputNumber, Modal, Select, Table, Tag, Tooltip, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, History, Lock, LockOpen, Pencil, Plus, Receipt, Scissors, Undo2 } from 'lucide-react';
import Layout from '../../Layout';
import { ktActor, ktApi, today, useKt } from './ktStore';
import {
  billById, billTotal, checkSequence, dayCloseErrors, dayTotals, dmy, fmtMoney, isLocked, modifyBlock, needsRef, PAY_METHOD, REF_LABEL, shiftCloseErrors, validatePatch,
  type Bill, type BillLine, type Payment,
} from './ktLogic';
import { BillStatusTag, CounterBar, DemoBar, InvoiceTag, KIND_LABEL, Kpi, PageHeader, Panel, ScopeTag, showErrors, SourceTag } from './ktUi';

/**
 * Một màn cho 2 giao diện (SRS mục I.4, 2.1, 3.2).
 * counter: quầy sửa, hủy bill trong ngày. internal: Kế toán nội bộ duyệt khóa sổ, Kế toán trưởng mở khóa.
 */
export default function KeToanBills({ mode = 'internal' }: { mode?: 'counter' | 'internal' }) {
  const s = useKt();
  const atCounter = mode === 'counter';
  const now = today();
  const dates = useMemo(() => Array.from(new Set([now, ...s.bills.map(b => b.date)])).sort().reverse(), [s.bills, now]);
  const [date, setDate] = useState(now);
  const [editing, setEditing] = useState<Bill | null>(null);
  const [cancelling, setCancelling] = useState<Bill | null>(null);
  const [logOf, setLogOf] = useState<string | null>(null);
  const [unlocking, setUnlocking] = useState(false);
  const [reason, setReason] = useState('');

  const t = dayTotals(s, date);
  const locked = isLocked(s, date);
  const closeErrors = [...dayCloseErrors(s, date), ...(locked ? [] : shiftCloseErrors(s, date))];
  const seqIssues = useMemo(() => checkSequence(s.bills), [s.bills]);
  const lock = s.locks[date];
  const dayLog = s.lockLog.filter(e => e.date === date);
  const logBill = s.bills.find(b => b.id === logOf) || null;
  const usageToday = s.usageLog.filter(e => e.date === date && !e.cancelled);

  const columns: ColumnsType<Bill> = [
    { title: 'Số bill', dataIndex: 'no', width: 110, render: (no: string, b) => <span className={`font-medium ${b.status === 'cancelled' ? 'line-through text-gray-400' : 'text-gray-900 dark:text-gray-100'}`}>{no}</span> },
    { title: 'Giờ', key: 'at', width: 64, render: (_, b) => dayjs(b.at).format('HH:mm') },
    { title: 'Khách, dịch vụ', key: 'c', render: (_, b) => (
      <div className="min-w-0">
        <div className="text-gray-900 dark:text-gray-100">{b.customer}</div>
        <div className="text-[11px] text-gray-500">{KIND_LABEL[b.kind]}: {b.lines.map(l => l.name).join(', ')}{b.parentBillId ? ` · theo ${billById(s, b.parentBillId)?.no || ''}` : ''}</div>
        <SourceTag bill={b} />
      </div>
    ) },
    { title: 'Số tiền', key: 'm', width: 130, align: 'right', render: (_, b) => (
      <div>
        <span className="font-medium">{fmtMoney(billTotal(b))}</span>
        {b.debt > 0 && <div className="text-[11px] text-amber-700">Còn nợ {fmtMoney(b.debt)}</div>}
        {b.refunds.length > 0 && <div className="text-[11px] text-rose-600">Hoàn {fmtMoney(b.refunds.reduce((t, r) => t + r.amount, 0))}</div>}
      </div>
    ) },
    { title: 'Thanh toán', key: 'p', width: 210, render: (_, b) => b.payments.map(p => (
      <div key={p.id} className="text-xs">
        <span className="text-gray-700 dark:text-gray-300">{PAY_METHOD[p.method]}{b.payments.length > 1 ? ` ${fmtMoney(p.amount)}` : ''}</span>
        {needsRef(p.method) && (p.ref
          ? <Tooltip title={REF_LABEL[p.method]}><span className="text-gray-400"> · {p.ref}</span></Tooltip>
          : b.status === 'valid' ? <span className="text-rose-600 font-medium"> · thiếu {REF_LABEL[p.method].toLowerCase()}</span> : null)}
      </div>
    )) },
    { title: 'Hóa đơn', key: 'i', width: 120, render: (_, b) => <InvoiceTag status={b.invoice.status} /> },
    { title: 'Trạng thái', key: 's', width: 90, render: (_, b) => <BillStatusTag bill={b} /> },
    { title: 'Phạm vi sổ', key: 'sc', width: 90, render: () => <ScopeTag scope="both" /> },
    { title: '', key: 'a', width: 120, align: 'right', render: (_, b) => {
      const block = modifyBlock(s, b, s.tier, now, ktActor(s));
      return (
        <div className="flex justify-end gap-1">
          {atCounter && <Tooltip title={block || 'Sửa bill'}><Button size="small" type="text" disabled={!!block} icon={<Pencil size={14} />} aria-label={`Sửa ${b.no}`} onClick={() => setEditing(b)} /></Tooltip>}
          {atCounter && <Tooltip title={block || 'Hủy bill'}><Button size="small" type="text" danger disabled={!!block} icon={<Undo2 size={14} />} aria-label={`Hủy ${b.no}`} onClick={() => { setReason(''); setCancelling(b); }} /></Tooltip>}
          <Tooltip title="Nhật ký"><Button size="small" type="text" icon={<History size={14} />} aria-label={`Nhật ký ${b.no}`} onClick={() => setLogOf(b.id)} /></Tooltip>
        </div>
      );
    } },
  ];

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title={atCounter ? 'Bill trong ngày' : 'Duyệt khóa sổ doanh thu'} desc={atCounter ? 'Sửa, hủy bill trong ngày có nhật ký. Kế toán nội bộ đã khóa sổ thì không sửa được nữa' : 'Kiểm tra đủ mã giao dịch và biên bản ca rồi duyệt khóa sổ; chỉ Kế toán trưởng mở lại được'}
          extra={(
            <>
              <Select value={date} onChange={setDate} className="w-64"
                options={dates.map(d => ({ value: d, label: `${dmy(d)}${d === now ? ' (hôm nay)' : ''}${s.locks[d] ? ' · đã khóa' : ''}` }))} />
              {atCounter && <Link to="/ke-toan/counter"><Button type="primary" icon={<Plus size={14} />}>Lập bill</Button></Link>}
            </>
          )} />
        <DemoBar portal={mode} />
        <CounterBar />

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <Kpi icon={<Receipt size={20} />} tone="bg-emerald-50 text-emerald-600" label="Thu trong ngày (gồm VAT)" value={fmtMoney(t.gross)} hint={`${t.valid.length} bill hợp lệ`} />
          <Kpi icon={<Scissors size={20} />} tone="bg-sky-50 text-sky-600" label="Chưa VAT" value={fmtMoney(t.net)} hint={`VAT ${fmtMoney(t.vat)}`} />
          <Kpi icon={<Pencil size={20} />} tone="bg-amber-50 text-amber-600" label="Bill đã sửa" value={t.edited} hint="Xem chi tiết trong nhật ký" />
          <Kpi icon={<Undo2 size={20} />} tone="bg-rose-50 text-rose-600" label="Bill đã hủy" value={t.cancelled} hint="Vẫn giữ số chứng từ" />
        </div>

        <Panel title={locked ? 'Sổ doanh thu đã khóa' : 'Sổ doanh thu đang mở'} icon={locked ? <Lock size={15} className="text-sky-600" /> : <LockOpen size={15} className="text-amber-600" />}
          extra={(
            <>
              {s.tier === 'revenue' && !locked && (
                <Button type="primary" size="small" icon={<Lock size={13} />} disabled={closeErrors.length > 0}
                  onClick={() => Modal.confirm({
                    title: `Duyệt khóa sổ ngày ${dmy(date)}?`, content: `${t.valid.length} bill, tổng ${fmtMoney(t.gross)}. Sau khi khóa, quầy không sửa hoặc hủy được nữa.`,
                    okText: 'Khóa sổ', cancelText: 'Thôi',
                    onOk: () => { const r = ktApi.lockDay(date); r.ok ? message.success('Đã khóa sổ') : showErrors(r.errors); },
                  })}>Duyệt khóa sổ doanh thu</Button>
              )}
              {s.tier === 'chief' && locked && <Button size="small" danger icon={<LockOpen size={13} />} onClick={() => { setReason(''); setUnlocking(true); }}>Mở khóa</Button>}
            </>
          )}>
          <div className="px-4 py-3 space-y-2 text-sm">
            {locked && lock && <div className="text-gray-600 dark:text-gray-300">{lock.by} khóa lúc {dayjs(lock.at).format('HH:mm DD/MM/YYYY')}. Bill trong ngày này không sửa, hủy hoặc chèn thêm được.</div>}
            {!locked && !closeErrors.length && <div className="flex items-center gap-1.5 text-emerald-700"><CheckCircle2 size={15} /> Đủ điều kiện khóa sổ{atCounter ? ': chờ Kế toán nội bộ duyệt' : s.tier !== 'revenue' ? ': đổi sang vai Kế toán nội bộ để duyệt' : ''}</div>}
            {!locked && closeErrors.map(e => <div key={e} className="flex items-center gap-1.5 text-rose-600"><AlertTriangle size={15} className="shrink-0" /> {e}</div>)}
            <div className="text-xs text-gray-500">
              Trừ thẻ trong ngày: {usageToday.length} lượt, số riêng trong <Link to={atCounter ? '/ke-toan/sessions' : '/ke-toan/cards'} className="text-[#3e91ab] hover:underline">nhật ký trừ thẻ</Link>, không dùng chung số bill và không cộng vào tiền thu.
            </div>
            {seqIssues.length > 0
              ? seqIssues.map(e => <div key={e} className="flex items-center gap-1.5 text-rose-600"><AlertTriangle size={15} className="shrink-0" /> {e}</div>)
              : <div className="text-xs text-gray-500">Số chứng từ liên tục, không đứt đoạn, không nhảy lùi ngày.</div>}
            {dayLog.some(e => e.action === 'unlock') && (
              <div className="text-xs text-gray-500">
                {dayLog.filter(e => e.action === 'unlock').map(e => <div key={e.at}>{e.by} mở khóa lúc {dayjs(e.at).format('HH:mm DD/MM/YYYY')}. Lý do: {e.reason}</div>)}
              </div>
            )}
          </div>
        </Panel>

        <Panel title={`Bill ngày ${dmy(date)} (${t.all.length})`}>
          <Table rowKey="id" size="middle" columns={columns} dataSource={[...t.all].sort((a, b) => a.seq - b.seq)} pagination={false} scroll={{ x: 1100 }} locale={{ emptyText: 'Ngày này chưa có bill' }} />
        </Panel>
      </div>

      {editing && <EditModal bill={editing} onClose={() => setEditing(null)} />}

      <Modal open={!!cancelling} title={`Hủy bill ${cancelling?.no}`} okText="Hủy bill" okButtonProps={{ danger: true }} cancelText="Quay lại" onCancel={() => setCancelling(null)} destroyOnHidden
        onOk={() => { const r = ktApi.cancelBill(cancelling!.id, reason); if (r.ok) { message.success('Đã hủy, số chứng từ được giữ nguyên'); setCancelling(null); } else showErrors(r.errors); }}>
        {cancelling?.invoice.status === 'issued' && <Alert type="warning" showIcon className="!mb-3" message={`Bill đã xuất hóa đơn ${cancelling.invoice.no}. Sau khi hủy, hệ thống tự báo để lập hóa đơn điều chỉnh.`} />}
        <label htmlFor="kt-cancel-reason" className="text-sm font-medium">Lý do hủy</label>
        <Input.TextArea id="kt-cancel-reason" rows={3} value={reason} onChange={e => setReason(e.target.value)} className="!mt-1" />
      </Modal>

      <Modal open={unlocking} title={`Mở khóa sổ ngày ${dmy(date)}`} okText="Mở khóa" okButtonProps={{ danger: true }} cancelText="Quay lại" onCancel={() => setUnlocking(false)} destroyOnHidden
        onOk={() => { const r = ktApi.unlockDay(date, reason); if (r.ok) { message.success('Đã mở khóa, thao tác được ghi lại'); setUnlocking(false); } else showErrors(r.errors); }}>
        <label htmlFor="kt-unlock-reason" className="text-sm font-medium">Lý do mở khóa</label>
        <Input.TextArea id="kt-unlock-reason" rows={3} value={reason} onChange={e => setReason(e.target.value)} className="!mt-1" />
      </Modal>

      <Drawer open={!!logBill} onClose={() => setLogOf(null)} title={`Nhật ký ${logBill?.no || ''}`} width={420}>
        <div className="space-y-4">
          {logBill && [...logBill.log].reverse().map(l => (
            <div key={`${l.at}${l.action}`} className="text-sm">
              <div className="flex justify-between gap-2"><b>{l.action}</b><span className="text-xs text-gray-400">{dayjs(l.at).format('HH:mm DD/MM/YYYY')}</span></div>
              <div className="text-xs text-gray-500">Người thực hiện: {l.by}</div>
              {l.reason && <div className="text-xs text-gray-700 dark:text-gray-300">Lý do: {l.reason}</div>}
              {l.changes?.map(c => <div key={c.field} className="text-xs mt-0.5"><span className="text-gray-500">{c.field}:</span> <Tag className="!mr-1">{c.from}</Tag>→ <Tag color="blue" className="!ml-1">{c.to}</Tag></div>)}
            </div>
          ))}
        </div>
      </Drawer>
    </Layout>
  );
}

function EditModal({ bill, onClose }: { bill: Bill; onClose: () => void }) {
  const [lines, setLines] = useState<BillLine[]>(bill.lines);
  const [payments, setPayments] = useState<Payment[]>(bill.payments);
  const [reason, setReason] = useState('');
  const errors = validatePatch({ lines, payments }, reason);
  const setLine = (id: string, gross: number) => setLines(ls => ls.map(l => (l.id === id ? { ...l, gross } : l)));
  const setPay = (id: string, p: Partial<Payment>) => setPayments(ps => ps.map(x => (x.id === id ? { ...x, ...p } : x)));
  const fmt = (v: number | string | undefined) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const parse = (v: string | undefined) => Number((v || '').replace(/\./g, ''));
  return (
    <Modal open title={`Sửa bill ${bill.no}`} okText="Lưu" cancelText="Thôi" onCancel={onClose} destroyOnHidden okButtonProps={{ disabled: errors.length > 0 }}
      onOk={() => { const r = ktApi.editBill(bill.id, { lines, payments }, reason); if (r.ok) { message.success('Đã lưu và ghi nhật ký'); onClose(); } else showErrors(r.errors); }}>
      <div className="space-y-3">
        {bill.invoice.status === 'issued' && <Alert type="warning" showIcon message={`Bill đã xuất hóa đơn ${bill.invoice.no}. Sửa tiền sẽ tự báo để lập hóa đơn điều chỉnh.`} />}
        {lines.map(l => (
          <div key={l.id}>
            <label htmlFor={`kt-line-${l.id}`} className="text-sm font-medium">Tiền {l.name}</label>
            <InputNumber id={`kt-line-${l.id}`} className="!w-full" min={0} step={100000} value={l.gross} onChange={v => setLine(l.id, Number(v) || 0)} formatter={fmt} parser={parse} />
          </div>
        ))}
        {payments.map(p => (
          <div key={p.id} className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor={`kt-pay-${p.id}`} className="text-sm font-medium">Thu {PAY_METHOD[p.method]}</label>
              <InputNumber id={`kt-pay-${p.id}`} className="!w-full" min={0} step={100000} value={p.amount} onChange={v => setPay(p.id, { amount: Number(v) || 0 })} formatter={fmt} parser={parse} />
            </div>
            <div>
              <label htmlFor={`kt-ref-${p.id}`} className="text-sm font-medium">{REF_LABEL[p.method]}</label>
              <Input id={`kt-ref-${p.id}`} disabled={!needsRef(p.method)} value={p.ref} onChange={e => setPay(p.id, { ref: e.target.value })} placeholder={needsRef(p.method) ? 'Bắt buộc' : 'Không cần'} />
            </div>
          </div>
        ))}
        <div>
          <label htmlFor="kt-edit-reason" className="text-sm font-medium">Lý do sửa</label>
          <Input.TextArea id="kt-edit-reason" rows={2} value={reason} onChange={e => setReason(e.target.value)} />
        </div>
        {errors.map(e => <div key={e} className="text-xs text-rose-600">{e}</div>)}
      </div>
    </Modal>
  );
}
